import * as React from 'react';
import * as echarts from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

import type {
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
  FunnelSeriesOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | FunnelSeriesOption
>;

echarts.use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  FunnelChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

export type SellerAcquisitionFunnelTotals = {
  market_landings: number;
  seller_register_clicks: number;
  seller_register_starts: number;
  seller_register_step2: number;
  sellers_created: number;
};

const SellerAcquisitionFunnelChart: React.FC<{ totals: SellerAcquisitionFunnelTotals }> = ({
  totals,
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    const data = [
      { name: 'Market landings', value: totals.market_landings || 0 },
      { name: 'Register clicks', value: totals.seller_register_clicks || 0 },
      { name: 'Registrations started', value: totals.seller_register_starts || 0 },
      { name: 'Step 2 (listing)', value: totals.seller_register_step2 || 0 },
      { name: 'Sellers created', value: totals.sellers_created || 0 },
    ];

    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      const option: ECOption = {
        tooltip: {
          trigger: 'item',
          formatter: (p: any) => `${p.name}: ${p.value}`,
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: data.map((d) => d.name),
        },
        series: [
          {
            name: 'Marketplace seller funnel',
            type: 'funnel',
            left: '28%',
            top: 20,
            bottom: 20,
            width: '58%',
            sort: 'none',
            gap: 4,
            label: {
              show: true,
              formatter: '{b}: {c}',
            },
            data,
          },
        ],
      };

      chartInstance.current.setOption(option);
    }

    const resizeHandler = () => chartInstance.current?.resize();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [totals]);

  return <div ref={chartRef} style={{ width: '100%', height: 340 }} />;
};

export default SellerAcquisitionFunnelChart;
